import { formatMoney } from "@hammer/core/money";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { api, clientFetch, type CaptureResponse } from "../lib/api.ts";
import { keys, useLots } from "../lib/queries.ts";
import { closePalette, palette, toast, useStore } from "../lib/store.ts";
import { absoluteTime } from "./lot-bits.tsx";

const isUrl = (s: string) => /^https?:\/\/\S+\.\S+/i.test(s.trim());

type Preview =
  | { state: "idle" }
  | { state: "loading" }
  | { state: "ready"; res: Extract<CaptureResponse, { status: "preview" }>; json?: string }
  | { state: "browser_failed" }
  | { state: "error"; message: string };

/** ⌘K: paste a lot URL to add it, or type to jump to a lot. */
export function Palette() {
  const { open, initial } = useStore(palette);
  if (!open) return null;
  return <PaletteInner initial={initial} />;
}

function PaletteInner({ initial }: { initial: string }) {
  const [q, setQ] = useState(initial);
  const [sel, setSel] = useState(0);
  const [preview, setPreview] = useState<Preview>({ state: "idle" });
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const lots = useLots().data ?? [];
  const url = isUrl(q) ? q.trim() : null;

  useEffect(() => input.current?.focus(), []);

  useEffect(() => {
    if (!url) return setPreview({ state: "idle" });
    let cancelled = false;
    setPreview({ state: "loading" });
    const t = setTimeout(async () => {
      try {
        let r = await api<CaptureResponse>("/capture", { method: "POST", body: { url, save: false } });
        let json: string | undefined;
        if (r.status === "needs_client") {
          const f = await clientFetch(r.request);
          if (cancelled) return;
          if (f.status !== 200) return setPreview({ state: "browser_failed" });
          json = f.body;
          r = await api<CaptureResponse>("/capture", { method: "POST", body: { url, json, save: false } });
        }
        if (cancelled) return;
        if (r.status === "preview") setPreview({ state: "ready", res: r, json });
        else if (r.status === "error") setPreview({ state: "error", message: r.message });
      } catch (e) {
        if (!cancelled) setPreview({ state: "error", message: (e as Error).message });
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [url]);

  const matches = useMemo(() => {
    if (url) return [];
    const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return lots.filter((l) => l.phase !== "closed").slice(0, 8);
    return lots.filter((l) => terms.every((t) => `${l.title} ${l.seller ?? ""} ${l.locationText ?? ""} ${l.tags.join(" ")}`.toLowerCase().includes(t))).slice(0, 8);
  }, [q, url, lots]);

  async function save() {
    if (!url || saving) return;
    if (preview.state === "ready" && preview.res.existingId) {
      closePalette();
      return navigate({ to: "/lot/$id", params: { id: preview.res.existingId } });
    }
    setSaving(true);
    try {
      let r: CaptureResponse | { status: "saved"; lotId: string } =
        await api<CaptureResponse>("/capture", { method: "POST", body: { url, json: preview.state === "ready" ? preview.json : undefined, save: true } });
      if (r.status === "needs_client") {
        // This browser couldn't reach the site either: track it by URL; the bookmarklet fills in the rest.
        const title = preview.state === "ready" ? preview.res.lot.title : decodeURIComponent(new URL(url).pathname.split("/").filter(Boolean).pop() ?? "Untitled lot").replace(/[-_]+/g, " ");
        r = await api<{ status: "saved"; lotId: string }>("/lots", { method: "POST", body: { url, title } });
      }
      if (r.status === "error") throw new Error(r.message);
      if (r.status !== "saved") throw new Error("Unexpected response");
      await qc.invalidateQueries({ queryKey: keys.lots });
      closePalette();
      toast("Added to your list");
      void navigate({ to: "/lot/$id", params: { id: r.lotId } });
    } catch (e) {
      toast(`Couldn't add it: ${(e as Error).message}`, { tone: "error" });
    } finally {
      setSaving(false);
    }
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "Escape") closePalette();
    else if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (url) void save();
      else if (matches[sel]) { closePalette(); void navigate({ to: "/lot/$id", params: { id: matches[sel]!.id } }); }
    }
  }

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && closePalette()}>
      <div className="dialog" role="dialog" aria-label="Add a lot or find one">
        <input
          ref={input}
          className="w-full bg-transparent px-5 py-4 text-[18px] outline-none border-b border-rule"
          placeholder="Paste a lot link to add it, or type to find one"
          value={q}
          onChange={(e) => { setQ(e.target.value); setSel(0); }}
          onKeyDown={onKey}
          aria-label="Lot link or search"
        />
        <div className="max-h-[55vh] overflow-auto">
          {url ? <UrlPreview p={preview} saving={saving} onSave={save} /> : (
            <ul role="listbox" className="py-1">
              {matches.map((l, i) => (
                <li key={l.id} role="option" aria-selected={i === sel}>
                  <button className={`w-full text-left px-5 py-2.5 flex justify-between gap-4 border-0 ${i === sel ? "bg-paper" : "bg-transparent"}`} onMouseEnter={() => setSel(i)} onClick={() => { closePalette(); void navigate({ to: "/lot/$id", params: { id: l.id } }); }}>
                    <span className="truncate">{l.title}</span>
                    <span className="muted num shrink-0">{formatMoney(l.latest?.currentBidCents)}</span>
                  </button>
                </li>
              ))}
              {!matches.length && <li className="px-5 py-4 muted">Nothing matches. Paste a lot link to add a new one.</li>}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function UrlPreview({ p, saving, onSave }: { p: Preview; saving: boolean; onSave: () => void }) {
  const now = Date.now();
  if (p.state === "loading") return <div className="px-5 py-4 muted">Reading the lot…</div>;
  if (p.state === "error") return <div className="px-5 py-4"><p className="text-red">{p.message}</p></div>;
  if (p.state === "browser_failed")
    return (
      <div className="px-5 py-4 grid gap-3">
        <p className="muted">This site didn't answer from this browser, so Hammer can't read the price yet. Add it anyway, then open the lot and click the Hammer bookmarklet to fill it in.</p>
        <div><button className="btn btn-primary" onClick={onSave} disabled={saving}>Add by link</button></div>
      </div>
    );
  if (p.state !== "ready") return <div className="px-5 py-4 muted">Paste a full link, starting with https://</div>;
  const { lot, obs, issues, existingId } = p.res;
  return (
    <div className="px-5 py-4 grid gap-3">
      <div className="flex gap-4">
        {lot.images?.[0] && <img src={lot.images[0]} alt="" className="w-24 h-18 object-cover rounded" referrerPolicy="no-referrer" />}
        <div className="min-w-0">
          <div className="font-[560] text-[16px] leading-snug">{lot.title}</div>
          <div className="muted text-[13.5px]">{lot.locationText}</div>
          <div className="mt-1 flex gap-4 num">
            <span className="board text-[18px]">{obs.currentBidCents != null ? formatMoney(obs.currentBidCents) : "No bids"}</span>
            {obs.endAt && <span className="muted self-end">ends {absoluteTime(obs.endAt, now)}</span>}
          </div>
        </div>
      </div>
      {issues.length > 0 && <ul className="text-[13.5px] text-amber list-disc pl-5">{issues.map((i) => <li key={i.detail}>{i.detail}</li>)}</ul>}
      <div className="flex gap-2 items-center">
        <button className="btn btn-primary" onClick={onSave} disabled={saving}>{existingId ? "Open it" : saving ? "Adding…" : "Add lot"}</button>
        <span className="faint text-[13px]">{existingId ? "You're already tracking this lot." : "Enter to add"}</span>
      </div>
    </div>
  );
}
